<template>
  <div class="bottom-panel-padding">
    <h3>Article Image</h3>
    <div
      class="card card-aside"
      v-if="!showCropping"
    >
      <span
        class="card-aside-column ml-5"
        :style="`background-image: url(${previewImage});background-size: contain;`"
        @mouseover="hover = true"
        @mouseleave="hover = false"
        :class="{ 'btn-loading': isLoading }"
      >
        <div
          class="btn-list text-center mt-3"
          v-if="hover && !shouldDisableDeleteImageButton && hasNewImagePreview"
        >
          <b-button
            size="sm"
            variant="danger"
            data-test="image-delete-button"
            class="ml-auto btn-pill"
            @click="deleteImage"
            :disabled="shouldDisableDeleteImageButton"
          >
            <i class="fa fa-trash-can mr-2" />
            Delete image
          </b-button>
        </div>
        <b-button
          v-if="showCropButton && hasNewImagePreview"
          variant="info"
          class="crop-button"
          @click="initCrop"
        >
          <i class="fa-light fa-crop" />
          <template v-if="hover">
            Crop image
          </template>
        </b-button>

      </span>
      <div class="card-body d-flex flex-column">
        <div class="form-group">
          <div class="form-label">
            File upload or url
          </div>
          <BaseImageInput
            v-model="newImage"
            ref="imageInput"
            :disabled="isCampaignReadonly"
          />
          <small>or</small>
          <b-form-input
            class="form-control"
            type="url"
            pattern="https?://.+"
            v-model="imageURL"
            @blur.native="updatePreviewImage"
            @keyup.enter.native="updatePreviewImage"
            placeholder="Image url"
            :state="validURL(imageURL)"
            trim
            :disabled="isCampaignReadonly"
          />
          <b-form-invalid-feedback
            id="input-live-feedback"
            data-test="image-url-invalid-feedback"
          >
            Please enter a valid Url
          </b-form-invalid-feedback>
        </div>
        <small class="text-muted">
          Maximum image size 5MB. Images larger then 300x250
          will be scaled to width and cropped from the center.
        </small>
      </div>
    </div>
    <div
      class="card mt-5"
      v-else
    >
      <div class="content card-body bg-white">
        <section class="cropper-area">
          <h6>Crop image</h6>
          <div class="img-cropper">
            <vue-cropper
              ref="cropper"
              :aspect-ratio="67 / 35"
              :src="contentImage + `?temp`"
              :check-orientation="false"
              preview=".preview"
              crossorigin="Anonymous"
            />
          </div>
        </section>
        <section class="preview-area">
          <p>Preview</p>
          <div class="preview" />
          <div class="d-flex w-100 mt-4">
            <b-button
              variant="tertiary"
              @click="closeCrop()"
            >
              Cancel
            </b-button>
            <b-button
              variant="primary"
              class="ml-auto"
              @click="cropImage()"
            >
              Save
            </b-button>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import VueCropper from 'vue-cropperjs';

import { mapActions, mapGetters, mapState } from 'vuex';
import * as types from '@/modules/content/store/types';
import * as campaignTypes from '@/modules/campaigns/store/types';

import 'cropperjs/dist/cropper.css';

export default {
  name: 'ImagePanel',
  components: { VueCropper },
  data() {
    return {
      isLoading: false,
      newImage: null,
      imageURL: '',
      previewImage: '',
      hover: false,
      showCropButton: false,
      showCropping: false,
      tempImage: null,
    };
  },
  props: {
    id: {
      type: Number,
      default: 0,
    },
    feedId: {
      type: Number,
      default: 0,
    },
    content: {
      type: Object,
      default: () => { },
    },
    creative: {
      type: Object,
      default: () => { },
    },
  },
  computed: {
    ...mapState('campaigns', [
      'selectedAdvertiser',
    ]),
    ...mapGetters('campaigns', {
      isCampaignReadonly: campaignTypes.GET_IS_CAMPAIGN_READONLY,
    }),
    contentImage() {
      return this.content.imageFileName ? this.content.imageFileName : '/images/image-not-set.png';
    },
    shouldDisableDeleteImageButton() {
      return (!this.content.imageFileName) || this.isCampaignReadonly;
    },
    hasNewImagePreview() {
      return (this.previewImage === this.content.imageFileName);
    },
  },
  methods: {
    ...mapActions('content', [
      types.UPLOAD_CONTENT_IMAGE,
      types.UPDATE_CONTENT_ITEM,
      types.DELETE_CONTENT_IMAGE,
      types.UPLOAD_CREATIVE_DISPLAY_IMAGE,
      types.UPDATE_CONTENT_DISPLAY_COPY,
      types.DELETE_CREATIVE_DISPLAY_IMAGE,
    ]),
    async submitImage() {
      this.isLoading = true;
      try {
        if (this.imageURL !== '') {
          const contentPayload = {
            advertisersId: this.selectedAdvertiser.id,
            creativeId: this.creative.id,
            feedId: this.feedId,
            id: this.content.encodedExternalId,
            body: {
              imageFileName: this.imageURL,
            },
          };
          await this.UPDATE_CONTENT_DISPLAY_COPY(contentPayload);
          this.$emit('refreshContentList');
          this.imageURL = '';
          this.$refs.imageInput.reset();
          this.isLoading = false;
        } else if (this.newImage !== null) {
          const { formData } = this.newImage;
          formData.set('body', new Blob([JSON.stringify({ contentItemUuid: this.content.encodedExternalId })], { type: 'application/json' }));
          const payload = {
            advertisersId: this.selectedAdvertiser.id,
            creativeId: this.creative.id,
            formData,
          };
          await this.UPLOAD_CREATIVE_DISPLAY_IMAGE(payload);
          this.$emit('refreshContentList');
          this.newImage = null;
          this.$refs.imageInput.reset();
          this.isLoading = false;
        }
      } catch (exception) {
        this.isLoading = false;
      }
    },
    async deleteImage() {
      this.isLoading = true;
      try {
        const payload = {
          advertisersId: this.selectedAdvertiser.id,
          creativeId: this.creative.id,
          contentItemId: this.content.encodedExternalId,
        };
        await this.DELETE_CREATIVE_DISPLAY_IMAGE(payload);
      } catch (exception) {
        this.isLoading = false;
      }
    },
    updatePreviewImage() {
      if (this.imageURL === '') this.previewImage = this.contentImage;
      if (!this.validURL(this.imageURL)) return;
      this.previewImage = this.imageURL;
    },
    validURL(str) {
      if (str === '') return null;
      const pattern = new RegExp('^(?:(?:https?|ftp):\\/\\/)?(?://([^/?#]*))?([^?#]*\\.(?:jpg|jpeg|png))(?:\\?([^#]*))?(?:#(.*))?', 'i');
      return (pattern.test(str));
    },
    initCrop() {
      this.showCropping = true;
    },
    b64toBlob(b64Data, contentType, sliceSize) {
      contentType = contentType || '';
      sliceSize = sliceSize || 512;
      const byteCharacters = atob(b64Data);
      const byteArrays = [];
      for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }
      return new Blob(byteArrays, { type: contentType });
    },

    cropImage() {
      const cropImg = this.$refs.cropper.getCroppedCanvas().toDataURL('image/jpeg');
      const block = cropImg.split(';');
      const contentType = block[0].split(':')[1];
      const fileType = contentType.split('/')[1];
      const realData = block[1].split(',')[1];
      const blob = this.b64toBlob(realData, contentType);
      const formData = new FormData();
      formData.append('file', blob, `image.${fileType}`);

      const payload = {
        advertisersId: this.selectedAdvertiser.id,
        feedId: this.feedId,
        contentItemId: this.id,
        formData,
      };
      this.UPLOAD_CONTENT_IMAGE(payload)
        .then(() => {
          this.$emit('refreshContentList');
          this.newImage = null;
          this.isLoading = false;
          this.showCropButton = true;
        }).catch(() => {
          this.isLoading = false;
        });
    },
    shouldShowCropButton() {
      this.showCropButton = !this.shouldDisableDeleteImageButton;
    },
    closeCrop() {
      this.showCropping = false;
    },
  },
  watch: {
    newImage(newValue) {
      this.hasNewImagePreview;
      this.shouldShowCropButton();
      this.showCropping = false;
      if (newValue !== null) {
        this.imageURL = '';
        this.newImage = newValue;
        this.previewImage = newValue.previewURL;
        this.submitImage();
      } else {
        this.updatePreviewImage();
      }
    },
    imageURL(newValue) {
      this.hasNewImagePreview;
      this.shouldShowCropButton();
      this.showCropping = false;
      if (newValue !== '') {
        this.newImage = null;
        this.imageURL = newValue;
        this.submitImage();
      }
    },
    contentImage() {
      this.shouldShowCropButton();
      this.showCropping = false;
      this.newImage = null;
      this.imageURL = '';
      if (this.contentImage !== '') this.previewImage = this.contentImage;
    },
  },
  mounted() {
    this.previewImage = this.contentImage;
    this.shouldShowCropButton();
  },
};
</script>
<style scoped>
.card-aside-column {
  position: relative;
}

.crop-button {
  position: absolute;
  bottom: 0.25rem;
  right: 0.25rem;
}

.content {
  display: flex;
  justify-content: space-between;
}

.cropper-area {
  width: 250px;
}

.actions {
  margin-top: 1rem;
}

.actions a {
  display: inline-block;
  padding: 5px 15px;
  background: #0062CC;
  color: white;
  text-decoration: none;
  border-radius: 3px;
  margin-right: 1rem;
  margin-bottom: 1rem;
}

textarea {
  width: 100%;
  height: 100px;
}

.preview-area {
  width: 180px;
}

.preview-area p {
  margin: 0;
  margin-bottom: 1rem;
}

.preview {
  width: 100%;
  height: calc(372px * (9 / 16));
  overflow: hidden;
}

.crop-placeholder {
  width: 100%;
  height: 200px;
  background: #ccc;
}

.cropped-image img {
  max-width: 100%;
}
</style>
